import { VisibilityOutlined } from '@mui/icons-material';
import { Grid, Box, Typography, FormControl, Tooltip, TextField, Button, type Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { createStyles, makeStyles } from '@mui/styles';
import { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ImportStateEnum } from '../../../../api/graphql/generated/schema';
import { EnumSelect } from '../../../../components';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formControl: {
      minWidth: 120,
      maxWidth: 300,
    },
    chips: {
      display: 'flex',
      flexWrap: 'wrap',
    },
    chip: {
      margin: 2,
    },
    noLabel: {
      marginTop: theme.spacing(3),
    },
  }),
);

export type Filters = {
  vin: string | null;
  brandId: string | null;
  states: Array<ImportStateEnum> | null;
};

const initFilterState = {
  vin: null,
  brandId: null,
  states: [ImportStateEnum.NoCard],
} satisfies Filters;

const QUERY_PARAM_NAMES = {
  VIN: 'vin',
  BRAND_ID: 'brandId',
  STATES: 'importVehicleStates',
} as const;

function fromStringIntoImportStateEnum(state: string): Array<ImportStateEnum> {
  const states = state
    .split(',')
    .filter((s) => Object.values(ImportStateEnum).includes(s as ImportStateEnum))
    .map((s) => s as ImportStateEnum);

  return states.length > 0 ? states : initFilterState.states;
}

type Props = {
  onFiltersChange: (filters: Filters) => void;
};

function ImportedVehicleFilters({ onFiltersChange }: Props) {
  const location = useLocation();
  const history = useHistory();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [vin, setFilterVin] = useState<string | null>(queryParams.get(QUERY_PARAM_NAMES.VIN) || initFilterState.vin);
  const [brandId, setFilterBrandId] = useState<string | null>(
    queryParams.get(QUERY_PARAM_NAMES.BRAND_ID) || initFilterState.brandId,
  );
  const [filterStates, setFilterStates] = useState<Array<ImportStateEnum>>(
    fromStringIntoImportStateEnum(queryParams.get(QUERY_PARAM_NAMES.STATES) || ''),
  );

  const classes = useStyles();
  const { t } = useTranslation();

  const submitFilter = () => {
    const nextParams = new URLSearchParams(location.search);
    nextParams.delete(QUERY_PARAM_NAMES.VIN);
    if (vin?.trim() && vin.trim()?.length > 0) {
      nextParams.set(QUERY_PARAM_NAMES.VIN, vin.trim());
    }

    nextParams.delete(QUERY_PARAM_NAMES.BRAND_ID);
    if (brandId?.trim() && brandId.trim()?.length > 0) {
      nextParams.set(QUERY_PARAM_NAMES.BRAND_ID, brandId.trim());
    }

    nextParams.delete(QUERY_PARAM_NAMES.STATES);
    if (filterStates.length > 0) {
      nextParams.set(QUERY_PARAM_NAMES.STATES, filterStates.join(','));
    }

    const searchStr = nextParams.toString();
    history.replace({ pathname: location.pathname, search: searchStr ? `?${searchStr}` : '' });
    onFiltersChange({ vin, brandId, states: filterStates.length > 0 ? filterStates : null });
  };

  const resetFilter = () => {
    setFilterVin(initFilterState.vin);
    setFilterBrandId(initFilterState.brandId);
    setFilterStates(initFilterState.states || []);
    const defaultFilters = new URLSearchParams([[QUERY_PARAM_NAMES.STATES, initFilterState.states.join(',')]]);
    history.replace({ pathname: location.pathname, search: `?${defaultFilters.toString()}` });
    onFiltersChange(initFilterState);
  };

  return (
    <Grid className='filter__holder'>
      <Box mb={3}>
        <Typography
          variant='h5'
          className='text-left'
        >
          {t('pages.definitionTable.filters.title')}
        </Typography>
      </Box>
      <Grid
        container
        direction='row'
        justifyContent='flex-start'
      >
        <Grid
          item
          xs={12}
          md={2}
          className='text-left'
        >
          <FormControl
            variant='outlined'
            className={classes.formControl}
          >
            <Tooltip title={t('pages.usedVehicleCards.filter.tooltips.asterisk')}>
              <TextField
                id={'card-filter-vin'}
                label={t('fieldName.vin')}
                type={'text'}
                name={'vin'}
                value={vin}
                onChange={(event) => setFilterVin(event.target.value)}
              />
            </Tooltip>
          </FormControl>
        </Grid>
        <Grid
          item
          xs={12}
          md={2}
          className='text-left'
        >
          <FormControl
            variant='outlined'
            className={classes.formControl}
          >
            <Tooltip title={t('pages.usedVehicleCards.filter.tooltips.asterisk')}>
              <TextField
                id={'card-filter-brandId'}
                label={t('fieldName.brand')}
                type={'text'}
                name={'brandId'}
                value={brandId}
                onChange={(event) => setFilterBrandId(event.target.value)}
              />
            </Tooltip>
          </FormControl>
        </Grid>
        <Grid
          item
          xs={12}
          sm={2}
          className='text-left'
        >
          <EnumSelect
            inputValues={[ImportStateEnum.NoCard, ImportStateEnum.CardPublished, ImportStateEnum.CardAwaitingApproval]}
            filteredValues={filterStates}
            setFilteredValues={setFilterStates}
            label={t('fieldName.state')}
            enumLabelsPath='importedVehicle.states'
            multiselect={true}
          />
        </Grid>
      </Grid>
      <Grid
        container
        direction='row'
        justifyContent='flex-start'
        component={'div'}
      >
        <Grid
          item
          xs={12}
          sm={12}
          className='text-right'
        >
          <Button
            className='reset-button'
            onClick={resetFilter}
          >
            {t('pages.definitionTable.filters.resetButton')}
          </Button>
          <Button
            className='icon-button'
            onClick={submitFilter}
          >
            <VisibilityOutlined />
            {t('pages.definitionTable.filters.searchButton')}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default ImportedVehicleFilters;
